import type {
    Breakpoint,
    PaperProps as PaperPropsType,
    SxProps,
    Theme,
} from '@mui/material';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import type { ForwardedRef, ReactElement } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeprecatedSimpleModal from 'ui/Modals/DeprecatedSimpleModal';

import {
    type ModalAction,
    type ModalStepT,
    type MultiStepModalRef,
} from './types';

export type { ModalStepT, MultiStepModalRef } from './types';

type Props = {
    isOpen?: boolean;
    onClose: () => void;
    maxWidth: Breakpoint;
    isSmallHeight?: boolean;
    showCloseIcon?: boolean;
    steps: ModalStepT[];
    PaperProps?: Partial<PaperPropsType>;
    className?: string;
    shouldRunStepBackOnReset?: boolean;
};

const defaultConfirm: Omit<ModalAction, 'onClick'> = {
    enabled: true,
    label: 'common.next',
    shouldTriggerDefaultOnClose: true,
};

const defaultCancel: Omit<ModalAction, 'onClick'> = {
    enabled: true,
    label: 'common.previous',
    shouldTriggerDefaultOnClose: true,
};

function MultiStepModal(
    {
        isOpen = undefined,
        onClose,
        maxWidth,
        isSmallHeight = undefined,
        showCloseIcon = false,
        steps,
        PaperProps = undefined,
        className = undefined,
        shouldRunStepBackOnReset = true,
    }: Props,
    ref: ForwardedRef<MultiStepModalRef>,
): ReactElement {
    const [stepIndex, setStepIndex] = useState(0);
    const { t } = useTranslation();

    // Expose methods via ref
    useImperativeHandle(
        ref,
        () => ({
            goToNextStep: () => {
                if (stepIndex + 1 < steps.length) {
                    setStepIndex(stepIndex + 1);
                }
            },
            goToPreviousStep: () => {
                if (stepIndex > 0) {
                    setStepIndex(stepIndex - 1);
                }
            },
            goToStep: (newStepIndex: number) => {
                if (newStepIndex >= 0 && newStepIndex < steps.length) {
                    setStepIndex(newStepIndex);
                }
            },
            getCurrentStep: () => stepIndex,
        }),
        [stepIndex, steps.length],
    );

    const resetStepIndex = () => {
        // TODO: refacto completely this logic
        // Running stepBackAction on reset can lead to weird behavior
        // where we reset some state before triggering a REST API call
        // This hacky bool has been added to prevent this behavior on NewInscriptionModal
        if (shouldRunStepBackOnReset) {
            const stepsToReset = steps.slice(0, stepIndex).reverse();
            stepsToReset.forEach((s) => {
                if (s.stepBackAction?.onClick !== undefined)
                    s.stepBackAction.onClick();
            });
        }
        setStepIndex(0);
    };

    useEffect(() => {
        if (!isOpen) {
            resetStepIndex();
        }
    }, [isOpen]);

    const {
        node,
        confirmAction,
        stepBackAction,
        autoClose,
        leftActionGroup,
        disableClose = false,
        title,
        isConfirmActionHidden,
        isStepBackActionHidden,
        PaperProps: stepPaperProps,
    } = steps[stepIndex];

    const mergedConfirmAction: ModalAction = {
        ...defaultConfirm,
        ...confirmAction,
    };
    const mergedStepBackAction: ModalAction = {
        ...defaultCancel,
        ...(stepIndex === 0 ? { label: 'common.close' } : {}),
        ...stepBackAction,
    };
    const mergedPaperProps: Partial<PaperPropsType> = {
        ...PaperProps,
        ...stepPaperProps,
        sx: [PaperProps?.sx, stepPaperProps?.sx].filter(
            Boolean,
        ) as SxProps<Theme>,
    };

    useEffect(() => {
        // Automatically triggers onClose() after 1500ms if conditions are met,
        // but clears the timeout if isOpen change before execution.
        let timeoutId: NodeJS.Timeout | undefined;

        if (autoClose && mergedConfirmAction.enabled && isOpen) {
            timeoutId = setTimeout(() => {
                onClose();
            }, 1500);
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [isOpen, autoClose, mergedConfirmAction.enabled]);

    const onStepCompleted = () => {
        if (mergedConfirmAction.onClick !== undefined)
            mergedConfirmAction.onClick();

        if (stepIndex + 1 < steps.length) {
            setStepIndex(stepIndex + 1);
        } else if (stepIndex + 1 === steps.length) {
            if (mergedConfirmAction.shouldTriggerDefaultOnClose) onClose();
            else resetStepIndex();
        }
    };

    const onStepCanceled = () => {
        if (mergedStepBackAction.onClick !== undefined)
            mergedStepBackAction.onClick();

        if (stepIndex === 0) {
            if (mergedStepBackAction.shouldTriggerDefaultOnClose) onClose();
            else resetStepIndex();
        } else {
            setStepIndex(stepIndex - 1);
        }
    };

    const cancelStepButton = !autoClose && (
        <Tooltip
            title={mergedStepBackAction.tooltipMessage}
            key="cancel-button"
        >
            <span>
                <Button
                    data-testid="e2e-multi-step-modal-cancel-button"
                    disabled={!mergedStepBackAction.enabled}
                    onClick={onStepCanceled}
                >
                    {t(mergedStepBackAction.label)}
                </Button>
            </span>
        </Tooltip>
    );

    const completeStepButton = !autoClose && (
        <Tooltip
            title={mergedConfirmAction.tooltipMessage}
            key="complete-button"
        >
            <span>
                <Button
                    data-testid="e2e-multi-step-modal-complete-button"
                    disabled={!mergedConfirmAction.enabled}
                    onClick={onStepCompleted}
                    variant="contained"
                    className={mergedConfirmAction.className}
                    color={mergedConfirmAction.color ?? 'primary'}
                >
                    {t(mergedConfirmAction.label)}
                </Button>
            </span>
        </Tooltip>
    );

    const additionalButtons = [
        isStepBackActionHidden ? null : cancelStepButton,
        isConfirmActionHidden ? null : completeStepButton,
    ].filter(Boolean);

    // TODO: Migrate to design-system modal
    return (
        <DeprecatedSimpleModal
            open={isOpen}
            onClose={onClose}
            maxWidth={maxWidth}
            isSmallHeight={isSmallHeight}
            additionalButtons={additionalButtons}
            leftActionGroup={leftActionGroup}
            showCloseButton={false}
            disableClosing={disableClose}
            dialogTitle={title}
            PaperProps={mergedPaperProps}
            className={className}
            showCloseIcon={showCloseIcon}
        >
            {node}
        </DeprecatedSimpleModal>
    );
}

// Wrap with forwardRef
export default forwardRef(MultiStepModal);
